import {
  getLocalStorageItem,
  setLocalStorageItem,
  removeLocalStorageItem,
  setRawLocalStorageItem,
  getRawLocalStorageItem,
} from "@shared-ui/utils/localStorage";

const STORAGE_KEY = "Cognito.rememberMe";
const LAST_LOGIN_ID_KEY = "Portal.lastLoginId";

interface RememberMeData {
  rememberMe: boolean;
}

export const setRememberMeToStorage = (rememberMe: boolean): void => {
  setLocalStorageItem<RememberMeData>(STORAGE_KEY, { rememberMe });
};

export const getRememberMeFromStorage = (): boolean => {
  return getLocalStorageItem<RememberMeData>(STORAGE_KEY, { rememberMe: false }).rememberMe;
};

export const clearRememberMeFromStorage = (): void => {
  removeLocalStorageItem(STORAGE_KEY);
};

export const setLastLoginId = (email: string): void => {
  setRawLocalStorageItem(LAST_LOGIN_ID_KEY, email);
};

export const getLastLoginId = (): string | null => {
  const value = getRawLocalStorageItem(LAST_LOGIN_ID_KEY, "");
  return value || null;
};

/**
 * PRODUCT_DEV-484 エイリアスログイン由来セッションの戻り先 tenantId。
 * sessionStorage に保持（タブを閉じると消える）。エイリアスURL (`/<tenantId>`) から
 * ポータルに入った場合、ポータル内のどの画面でログアウトしても
 * エイリアスログイン画面へ戻すために使う。
 */
const ALIAS_RETURN_TENANT_KEY = "Portal.aliasReturnTenant";

export const setAliasReturnTenant = (tenantId: string): void => {
  try {
    sessionStorage.setItem(ALIAS_RETURN_TENANT_KEY, tenantId);
  } catch {
    // ignore (sessionStorage 不可環境)
  }
};

export const getAliasReturnTenant = (): string | null => {
  try {
    return sessionStorage.getItem(ALIAS_RETURN_TENANT_KEY) || null;
  } catch {
    return null;
  }
};

/**
 * PRODUCT_DEV-1121 このブラウザでパスキーを登録した・パスキーでログインできたログインID の印。
 *
 * サーバーは「この端末にパスキーがあるか」をブラウザと OS の種類（`Chrome:Windows` など）でしか覚えていないので、
 * 同じ種類のブラウザならシークレットウインドウ・別のプロファイル・別の PC でも「ある」と返す。
 * そこで自動起動すると、OS の「利用可能なパスキーがありません」や QR コードの画面が出てしまう。
 * 自動で起動してよいか・ログイン後にパスキーの登録を聞くかは、**このブラウザに残した印**で決める
 * （localStorage なのでプロファイルごと。シークレットウインドウには残らない）。
 *
 * 印にはパスキーの ID と種類（`authenticatorAttachment`）も持つ（10/14 日暮さん決定）。
 * 種類が `cross-platform`（スマホを QR で読んで登録したものなど）のときは印を付けない。
 * 10/13 版の印（ログイン ID の文字列だけ）も読める。
 */
const PASSKEY_LOGIN_IDS_KEY = "Portal.passkeyLoginIds";
const PASSKEY_LOGIN_IDS_MAX = 20;

export type PasskeyMark = {
  loginId: string;
  /** 印を付けたパスキーの ID（分からないときは無い。10/13 版の印・パスキーでのログイン成功時） */
  credentialId?: string;
  /** `platform` / `cross-platform`。分からないときは無い */
  attachment?: string;
};

const normalizeLoginId = (loginId: string): string => loginId.trim().toLowerCase();

const readPasskeyMarks = (): PasskeyMark[] =>
  getLocalStorageItem<unknown[]>(PASSKEY_LOGIN_IDS_KEY, []).flatMap((v): PasskeyMark[] => {
    if (typeof v === "string") return v ? [{ loginId: v }] : [];
    if (v && typeof v === "object" && typeof (v as PasskeyMark).loginId === "string") {
      return [v as PasskeyMark];
    }
    return [];
  });

const writePasskeyMarks = (marks: PasskeyMark[]): void => {
  if (marks.length === 0) {
    removeLocalStorageItem(PASSKEY_LOGIN_IDS_KEY);
    return;
  }
  setLocalStorageItem<PasskeyMark[]>(PASSKEY_LOGIN_IDS_KEY, marks.slice(0, PASSKEY_LOGIN_IDS_MAX));
};

export const hasPasskeyOnThisBrowser = (loginId: string | null | undefined): boolean => {
  if (!loginId) return false;
  const id = normalizeLoginId(loginId);
  return readPasskeyMarks().some((m) => m.loginId === id && m.attachment !== "cross-platform");
};

/**
 * 印を付ける。`attachment` が `cross-platform` なら付けない（この端末のパスキーではないので、
 * 自動で起動すると QR コードになる）。同じ ID の印は新しいものに置き換える。
 */
export const markPasskeyOnThisBrowser = (
  loginId: string | null | undefined,
  detail?: { credentialId?: string; attachment?: string },
): void => {
  if (!loginId) return;
  const id = normalizeLoginId(loginId);
  if (!id) return;
  if (detail?.attachment === "cross-platform") return;
  const prev = readPasskeyMarks().find((m) => m.loginId === id);
  const others = readPasskeyMarks().filter((m) => m.loginId !== id);
  const mark: PasskeyMark = {
    loginId: id,
    // パスキーでのログイン成功時は ID・種類が分からないので、前の印の分を残す
    ...((detail?.credentialId ?? prev?.credentialId)
      ? { credentialId: detail?.credentialId ?? prev?.credentialId }
      : {}),
    ...((detail?.attachment ?? prev?.attachment)
      ? { attachment: detail?.attachment ?? prev?.attachment }
      : {}),
  };
  writePasskeyMarks([mark, ...others]);
};

/**
 * 印を消す。
 * - `loginId`: その ID の印（パスキーでのログインが「登録なし」で失敗したとき）
 * - `credentialId`: そのパスキーの印と、パスキーの ID を持たない印（どれを消したか分からないので）
 * - どちらも無し: 全部
 */
export const clearPasskeyOnThisBrowser = (
  target?: { loginId?: string | null; credentialId?: string },
): void => {
  if (target?.loginId) {
    const id = normalizeLoginId(target.loginId);
    writePasskeyMarks(readPasskeyMarks().filter((m) => m.loginId !== id));
    return;
  }
  if (target?.credentialId) {
    writePasskeyMarks(
      readPasskeyMarks().filter((m) => m.credentialId && m.credentialId !== target.credentialId),
    );
    return;
  }
  removeLocalStorageItem(PASSKEY_LOGIN_IDS_KEY);
};
